import {
  ErrorInfo,
  isValidElement,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import Banner, { BannerProps } from './banner/banner';
import Footer from './footer/footer';
import { PagedivProps } from './PageHeader/PageHeader';
import { BannerCTA } from '@/components/Buttons/BannerCTA/bannerCTA';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { InfoNotification } from '@/components/toast/toastMessages';
import cntl from 'cntl';
import useUser from '@/hooks/useUser';
import { AnimatePresence, motion } from 'framer-motion';
import LoadingSpinner from '@/components/Loaders/PageLoader/loadingSpinner';
import Error404 from '../Error404/Error404';
import { ErrorBoundary } from 'react-error-boundary';
import useLocalCart from '@/hooks/useLocalCart';
import useThowError from '@/hooks/useThowError';
import MobileBannerCTA from '@/components/Buttons/BannerCTA/MobileBannerCTA';

const getMainContentCn = (
  hideHeader: boolean,
  isHeroHeader: boolean,
  hasPageError: boolean,
  mainClassName: string,
) => cntl`
  flex-1 
  flex 
  flex-col 
  w-full 
  focus:outline-none 
  focus-visible:ring-2 
  focus-visible:ring-blue-500
  ${!hideHeader && !isHeroHeader ? '[&>*:first-child]:mt-[80px]' : ''}
  ${hasPageError ? 'min-h-[100dvh] max-h-[100dvh] bg-brand-gold' : mainClassName}
`;

type SlotRecord = Record<
  string,
  {
    variant?: BannerProps['variant'];
    header?: ReactElement<PagedivProps>;
    node: ReactNode;
    authRequired?: boolean;
  }
>;

export interface PageProps {
  children?: ReactNode | SlotRecord;
  pageHeader?: ReactElement<PagedivProps>;
  className?: string;
  mainClassName?: string;
  sectionClassName?: string;
  variant?: BannerProps['variant'];
  disableCTA?: BannerProps['disableCTA'];
  hideFooter?: boolean;
  hideHeader?: boolean;
  protect?: boolean;
}

// Type Guard to safely check if children is a SlotRecord dictionary
const isSlotRecord = (children: any): children is SlotRecord => {
  return (
    children &&
    typeof children === 'object' &&
    !isValidElement(children) &&
    !Array.isArray(children)
  );
};

const ErrorListener = () => {
  const {} = useThowError();
  return (
    <div
      id="err-listener"
      className="h-0 w-0 absolute opacity-0 pointer-events-none"
    />
  );
};

const PageContainer = ({
  children,
  className = '',
  disableCTA: ctaIsDisabled = false,
  hideFooter: footerIsHidden = false,
  hideHeader = false,
  activeBannerVariant,
  childKey,
  isHeroHeader,
  mainClassName = '',
  hasPageError,
  setHasPageError,
}: PageProps & {
  activeBannerVariant: BannerProps['variant'];
  isHeroHeader: boolean;
  childKey: string;
  children: ReactNode;
  hasPageError: boolean;
  setHasPageError: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const disableCTA = ctaIsDisabled || hasPageError;
  const hideFooter = footerIsHidden || hasPageError;
  const variant = hasPageError ? 'dark' : activeBannerVariant;

  // 1. Define the callback
  const handleCatchError = useCallback(
    (error: unknown, info: ErrorInfo) => {
      // Update your parent state
      setHasPageError(true);

      // You can also log the error to a reporting service here
      console.error('Page layout caught an error:', error, info.componentStack);
    },
    [setHasPageError],
  );

  // 2. Define a reset callback (Crucial!)
  const handleResetError = useCallback(() => {
    // If your FallbackComponent has a "Try Again" button that resets the boundary,
    // you need to ensure your parent state resets as well.
    setHasPageError(false);
  }, [setHasPageError]);
  return (
    <div
      id="page-cn"
      className={`relative w-full max-w-[100vw] overflow-x-hidden min-h-screen flex flex-col bg-brand-cream ${className}`}
    >
      {/* Banner automatically receives 'transparent' if a hero header is present */}
      {(!hideHeader || hasPageError) && (
        <Banner variant={variant} disableCTA={disableCTA} />
      )}
      {/* --- NEW: AnimatePresence Transition Wrapper --- */}
      <AnimatePresence mode="wait">
        <motion.div
          key={childKey}
          initial={{ opacity: 0, scale: 0.98, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 1.02, y: -10 }}
          transition={{
            duration: 0.4,
            ease: [0.22, 1, 0.36, 1], // High-end custom easing curve (Apple-style)
          }}
          className="w-full h-full"
        >
          <main
            id="page-content-cn"
            className={getMainContentCn(
              hideHeader,
              isHeroHeader,
              hasPageError,
              mainClassName,
            )}
            tabIndex={-1} // <--- ADD THIS: Allows programmatic focus without breaking tab order
          >
            {/* Ideally, wrap {children} in an ErrorBoundary here */}
            <ErrorBoundary
              FallbackComponent={Error404}
              resetKeys={[childKey]}
              onError={handleCatchError} // <--- Triggers when an error is caught
              onReset={handleResetError} // <--- Triggers when the boundary recovers
            >
              {children}
              <ErrorListener />
            </ErrorBoundary>
          </main>
        </motion.div>
      </AnimatePresence>

      {!hideFooter && <Footer />}

      <MobileBannerCTA bannerVariant={variant} hidden={disableCTA} />
    </div>
  );
};

export default ({
  children,
  pageHeader,
  variant = 'primary',
  protect: protectProp = false,
  ...props
}: PageProps) => {
  const [searchParams] = useSearchParams();
  const { user, checkSession } = useUser();
  const {} = useLocalCart();
  const [hasPageError, setHasPageError] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();

  // 1. Safely Resolve Children
  let resolvedChildren: ReactNode = null;
  let resolvedKey: string = 'default';
  let resolvedHeader: ReactElement<PagedivProps> | undefined = pageHeader;
  let resolvedVariant: BannerProps['variant'] = variant;
  let protect = protectProp;

  // Track if we are in a "Slot Record but no match" scenario
  let requiresRedirect = false;

  if (isSlotRecord(children)) {
    let matchFound = false;
    // Iterate through EVERY value in the URL query string
    // e.g., ?step=shipping&promo=123 -> values are ['shipping', '123']
    for (const urlValue of searchParams.values()) {
      if (children[urlValue]) {
        resolvedChildren = children[urlValue].node;
        resolvedHeader = children[urlValue].header ?? resolvedHeader;
        resolvedVariant = children[urlValue].variant ?? resolvedVariant;
        protect = children[urlValue]?.authRequired ?? protect;
        resolvedKey = urlValue;
        matchFound = true;
        break; // Stop at the first match to prevent overriding
      }
    }
    // --- NEW: The Fallback Safety Net ---
    // If no URL matched, but the developer provided a 'default' slot, use it.
    if (!matchFound && children['default']) {
      resolvedChildren = children['default'].node;
      resolvedHeader = children['default'].header ?? resolvedHeader;
      resolvedVariant = children['default'].variant ?? resolvedVariant;
    }
    // If it's a slot layout and the URL param is missing/invalid, flag it.
    else if (!matchFound) {
      requiresRedirect = true;
    }
  } else {
    // It's standard ReactNode (string, array, single element, null)
    resolvedChildren = children as ReactNode;
  }

  const { sectionClassName = '' } = props;

  // Check if current path is exactly /login
  const isLoginPage = location.pathname === '/login';

  // --- NEW: Auth Verification State ---
  // Start loading by default ONLY if the page is protected.
  const [isVerifying, setIsVerifying] = useState<boolean>(protect);

  // --- NEW: Unified Auth & Session Check ---
  useEffect(() => {
    let isMounted = true;

    const verifyAuth = async () => {
      try {
        const activeSession = await checkSession();

        // If the page is protected and there is no session, redirect.
        if (protect && !activeSession && isMounted) {
          const currentUrl = location.pathname + location.search;
          const encodedRedirect = encodeURIComponent(currentUrl);
          navigate(`/login?redirect=${encodedRedirect}`, { replace: true });
        }
      } catch (error) {
        console.error('[AUTH_ERROR]: Failed to verify session.', error);
        if (protect && isMounted) {
          navigate('/login', { replace: true });
        }
      } finally {
        if (isMounted) setIsVerifying(false);
      }
    };

    // If we already have a user, or if the page isn't protected, we don't need to block render.
    if (user?.id || !protect) {
      setIsVerifying(false);
      // We still run checkSession in the background to hydrate the user object for non-protected pages
      if (!user?.id) checkSession();
    } else {
      verifyAuth();
    }

    return () => {
      isMounted = false;
    };
  }, [
    protect,
    user?.id,
    location.pathname,
    location.search,
    navigate,
    checkSession,
  ]);

  // --- THE REDIRECT EFFECT ---
  useEffect(() => {
    if (requiresRedirect) {
      // 1. Fire the warm, professional toast
      InfoNotification({
        title: 'Page Not Found',
        message: "We've guided you back to the home page to start fresh.",
        theme: 'tinted', // Blue outlined theme for a helpful, non-alarmist vibe
      });

      // 2. Redirect to root.
      // 'replace: true' is CRITICAL so they don't get stuck in a Back-Button loop!
      navigate('/', { replace: true });
    }
  }, [requiresRedirect, navigate]);

  useEffect(() => {
    if (!isVerifying) {
      // Change 'smooth' to 'auto' for crisp, native-feeling page transitions
      window.scrollTo({ top: 0, behavior: 'auto' });

      // Shift focus to the main container for Screen Readers
      const mainContent = document.getElementById('page-content-cn');
      if (mainContent) {
        mainContent.focus();
      }
    }
  }, [resolvedKey, isVerifying]);

  const isHeroHeader =
    (resolvedHeader?.type as any)?.variant === 'hero' ||
    resolvedHeader?.props?.variant === 'hero';

  const activeBannerVariant = isHeroHeader ? 'transparent' : resolvedVariant;

  // --- NEW: AUTH BLOCKING RENDER ---
  if (protect && isVerifying) {
    return (
      <PageContainer
        {...props}
        activeBannerVariant={activeBannerVariant}
        childKey={resolvedKey}
        isHeroHeader={isHeroHeader}
        hasPageError={hasPageError}
        setHasPageError={setHasPageError}
        disableCTA
        hideFooter
      >
        <div className="flex flex-1 w-[100vw] h-screen items-center justify-center bg-brand-cream">
          <LoadingSpinner global message="Verifying Session..." />
        </div>
      </PageContainer>
    );
  }

  // --- THE BLANK STATE RENDER ---
  // If we flagged a slot redirect OR if verification failed but the navigate hasn't fired yet
  if (requiresRedirect || (protect && !user?.id)) return null;

  return (
    <PageContainer
      {...props}
      activeBannerVariant={activeBannerVariant}
      childKey={resolvedKey}
      isHeroHeader={isHeroHeader}
      hasPageError={hasPageError}
      setHasPageError={setHasPageError}
    >
      {/* Render the optional decoupled header component */}
      {resolvedHeader}

      <section
        id="page-cn-section"
        className={`w-full min-h-screen focus:border-0 relative flex flex-col pb-10 px-6 md:px-10 ${hasPageError ? '!p-0 bg-brand-gold justify-center md:justify-start !min-h-0 h-full overflow-hidden' : sectionClassName}`}
      >
        {resolvedChildren}
      </section>
    </PageContainer>
  );
};
